import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import Slide from '@mui/material/Slide';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Box from '@mui/material/Typography';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const EditTenantModal = ({ open, handleClose, tenant }) => {
    const [formData, setFormData] = useState({
        name: '',
        flatNumber: '',
        building: '',
        society: '',
        phone: '',
        email: '',
    });
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (tenant) {
            setFormData({
                name: tenant.name || '',
                flatNumber: tenant.flatNumber || '',
                building: tenant.building || '',
                society: tenant.society || '',
                phone: tenant.phone || '',
                email: tenant.email || '',
            });
            setErrors({});
        }
    }, [tenant, open]);

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = 'Tenant name is required';
        if (!formData.flatNumber.trim()) newErrors.flatNumber = 'Flat number is required';
        if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Invalid email format';
        }

        return newErrors;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleSubmit = async () => {
        const newErrors = validateForm();
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setIsLoading(true);
        try {
            // TODO: Implement API call to update tenant
            console.log('Updating tenant:', formData);
            handleClose();
        } catch (error) {
            console.error('Error updating tenant:', error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            TransitionComponent={Transition}
            keepMounted
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle
                sx={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    position: 'relative',
                    overflow: 'hidden',
                    p: 2.5,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}
            >
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                    Edit Tenant
                </Typography>
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    size="small"
                    sx={{
                        color: 'white',
                        backgroundColor: 'rgba(255,255,255,0.15)',
                        '&:hover': {
                            backgroundColor: 'rgba(255,255,255,0.25)',
                        },
                    }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 3, backgroundColor: '#f8fafc' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                    <TextField
                        label="Tenant Name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        size="small"
                        fullWidth
                        error={!!errors.name}
                        helperText={errors.name}
                        disabled={isLoading}
                    />

                    <TextField
                        label="Flat Number"
                        name="flatNumber"
                        value={formData.flatNumber}
                        onChange={handleChange}
                        size="small"
                        fullWidth
                        error={!!errors.flatNumber}
                        helperText={errors.flatNumber}
                        disabled={isLoading}
                    />

                    <TextField
                        label="Building"
                        name="building"
                        value={formData.building}
                        onChange={handleChange}
                        size="small"
                        fullWidth
                        disabled={isLoading}
                    />

                    <TextField
                        label="Society"
                        name="society"
                        value={formData.society}
                        onChange={handleChange}
                        size="small"
                        fullWidth
                        disabled={true}
                    />

                    <TextField
                        label="Email Address"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        size="small"
                        fullWidth
                        error={!!errors.email}
                        helperText={errors.email}
                        disabled={isLoading}
                    />

                    <TextField
                        label="Phone Number"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        size="small"
                        fullWidth
                        error={!!errors.phone}
                        helperText={errors.phone}
                        disabled={isLoading}
                    />
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2, gap: 1 }}>
                <Button
                    variant="outlined"
                    onClick={handleClose}
                    disabled={isLoading}
                    sx={{ textTransform: 'capitalize' }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={isLoading}
                    sx={{
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        textTransform: 'capitalize',
                    }}
                >
                    {isLoading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
                    Save Changes
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditTenantModal;
